"use client";

import * as React from "react";
import { formatUsd } from "../../model/sendFormat";
import type { SendFlow } from "../../model/useSendFlow";
import { parseTokenAmount } from "../../utils";

export function SendAmount({ flow }: { flow: SendFlow }) {
  const { amount, setAmount, amountUsd, balanceText, balanceRaw, token, setMax } =
    flow;
  const inputRef = React.useRef<HTMLInputElement>(null);

  const moveCursorToEnd = React.useCallback(() => {
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      const len = el.value.length;
      el.setSelectionRange(len, len);
    });
  }, []);

  // 진입 시 포커스 + 커서 끝
  React.useEffect(() => {
    inputRef.current?.focus();
    moveCursorToEnd();
  }, [moveCursorToEnd]);

  // amount가 빈 값이 되면 "0.00" 재표시 + 커서 끝
  React.useEffect(() => {
    if (amount === "") {
      setHasInteracted(false);
      moveCursorToEnd();
    }
  }, [amount, moveCursorToEnd]);

  const overBalance = React.useMemo(() => {
    const parsed = parseTokenAmount(amount, token.quantity.decimals);
    return "value" in parsed && parsed.value > balanceRaw;
  }, [amount, token.quantity.decimals, balanceRaw]);

  const [hasInteracted, setHasInteracted] = React.useState(false);
  const displayAmount = !hasInteracted && amount === "" ? "0.00" : amount;
  const empty = amount.trim() === "" || amount.trim() === "0";

  return (
    <div className="sf-body sf-amount">
      <div className="sf-amount-center">
        <span className="sf-amount-usd">
          {amountUsd !== undefined ? formatUsd(amountUsd) : " "}
        </span>
        <div
          className="sf-amount-input-wrap"
          data-over={overBalance || undefined}
          data-empty={empty || undefined}
          onClick={() => inputRef.current?.focus()}
        >
          <input
            ref={inputRef}
            className="sf-amount-input"
            value={displayAmount}
            onChange={(e) => {
              const v = e.target.value;
              if (!hasInteracted) {
                if (amount === "") {
                  // 플레이스홀더 "0.00" 상태 — 백스페이스면 유지, 새 입력이면 "0.00" 제거
                  if (v.length < 4) return;
                  setHasInteracted(true);
                  const stripped = v.startsWith("0.00") ? v.slice(4) : v;
                  setAmount(stripped);
                } else {
                  // setMax 등으로 채워진 상태 — 바로 편집 허용
                  setHasInteracted(true);
                  setAmount(v);
                }
              } else {
                setAmount(v);
              }
            }}
            placeholder="0.00"
            inputMode="decimal"
            autoComplete="off"
            aria-label={`Amount in ${token.symbol}`}
            size={1}
          />
        </div>
        <div className="sf-amount-balance">
          <span>Balance:</span>
          <button
            type="button"
            className="sf-amount-max"
            onClick={setMax}
            data-track="amount_max"
          >
            {balanceText} {token.symbol}
          </button>
        </div>
      </div>
    </div>
  );
}
